import './PlaceDetailScreen.scss';
import '../../scss/variables.scss';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { Button, Card, Col, Row, Tab, Tabs } from 'react-bootstrap';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
  BeaconReadingsCellRenderer,
  DetailOptionsOverlay,
  Icon,
  LinkCellRenderer,
  Loading,
  OnlineStatusCellRenderer,
  Text,
  toast,
} from '../../components';
import { PagedQueryParams, ServerSideGrid } from '../../components/grids/AgGrid/ServerSideGrid';
import { ABILITIES, ABILITY_MAP } from '../../constants';
import LocationMap from '../../modules/location/components/locationMap';
import { useApp } from '../../react';
import { history } from '../../redux';
import { Api, authService, favoriteService } from '../../services';
import { isReadOnly } from '../../utils';
import { assetColumnDefs, gatewaysColumnDefs, stationaryAssetsColumnDefs } from './columnDefs';

const MySwal = withReactContent(Swal);

const TAB_KEYS_MAP = {
  ASSETS: 'Assets',
  STATIONARY_ASSETS: 'Stationary Assets',
  GATEWAYS: 'Gateways',
};

const TAB_KEYS = Object.values(TAB_KEYS_MAP);

// #region PURE MAP LOCATION HELPERS
const getAssetLocations = (assets, map) => {
  return (
    assets?.items?.map((asset) => {
      const coords = asset.location?.coordinates;
      return {
        location: coords ? { x: coords.x, y: coords.y, type: 0 } : null,
        isVisible: true,
        mapId: map?.id,
        name: asset.name || asset.assetName,
        onmouseover: (e) => e.marker.openPopup(),
        color:
          asset.status === 1
            ? 'var(--c-primary)'
            : asset.status === 2
            ? 'var(--warning)'
            : asset.status === 3
            ? 'var(--secondary)'
            : 'var(--danger)',
        fillColor:
          asset.status === 1
            ? 'var(--c-primary)'
            : asset.status === 2
            ? 'var(--warning)'
            : asset.status === 3
            ? 'var(--secondary)'
            : 'var(--danger)',
        asset,
      };
    }) || []
  );
};

const getStationaryAssetsLocations = (stationaryAssets) => {
  return (
    stationaryAssets?.items?.map((currSAsset, index) => {
      const location = {
        type: 0,
        x: currSAsset.coordinates?.coordinates?.x,
        y: currSAsset.coordinates?.coordinates?.y,
      };
      const sAsset = { ...currSAsset, Location: location };
      currSAsset.readings?.forEach((reading) => {
        sAsset[reading.ability] = reading;
      });

      return {
        sAsset,
        index,
        isVisible: true,
        location,
        autoPopup: true,
      };
    }) || []
  );
};

const getGatewayLocations = (gateways) => {
  return (
    gateways?.items?.map((gateway, index) => ({
      gateway,
      isVisible: true,
      location: {
        type: 0,
        x: gateway.coordinates?.x,
        y: gateway.coordinates?.y,
      },
      index,
      autoPopup: true,
    })) || []
  );
};
// #endregion

function PlaceDetailScreen(props) {
  const placeId = props.match.params.placeId;
  const placeName = props.location.state?.placeName || '';

  const [tabKey, setTabKey] = useState(TAB_KEYS_MAP.ASSETS);
  const [abilityTabKey, setAbilityTabKey] = useState(ABILITY_MAP.Temperature.name);
  const [mapLayers, setMapLayers] = useState([]);
  const [isFavorite, setIsFavorite] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [place, setPlace] = useState(null);
  const [map, setMap] = useState(null);
  const [gateways, setGateways] = useState(null);
  const [stationaryAssets, setStationaryAssets] = useState(null);
  const [assets, setAssets] = useState(null);
  const [error, setError] = useState(null);

  const app = useApp();
  const previousStationarySearchRef = useRef('');

  const defaultAssetFilter = [{ field: 'placeId', type: 'equal', filter: placeId }];
  const assetFilters = useMemo(
    () =>
      authService
        .ensureAssetFiltersInLocalStorage()
        .concat(JSON.parse(localStorage.getItem('tempFilters') || '[]')),
    [],
  );

  // Derived state for map locations
  const assetLocations = useMemo(() => getAssetLocations(assets, map), [assets, map]);
  const stationaryAssetsLocations = useMemo(
    () => getStationaryAssetsLocations(stationaryAssets),
    [stationaryAssets],
  );
  const gatewayLocations = useMemo(() => getGatewayLocations(gateways), [gateways]);

  useEffect(() => {
    getPlaceData();
    checkIsFavorite();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Getting Place & map data
  const getPlaceData = async () => {
    setIsLoading(true);
    try {
      const { data: placeInfo } = await Api.get(`/v2/places/${placeId}`);
      setPlace(placeInfo);

      // Map image
      const { data: mapData } = await Api.get(`/maps/${placeInfo.mapId}/layout?trackView=false`);
      setMap(mapData);

      // Stationary & Gateways
      const [stationaryAssetsResponse, gatewaysResponse] = await Promise.all([
        Api.post(`v2/places/${placeId}/stationary-assets`, {
          sorts: [{ field: 'assetName', ascending: true }],
        }),
        Api.post(`v2/places/${placeId}/gateways/filter`, {
          sorts: [{ field: 'name', ascending: true }],
        }),
      ]);

      setStationaryAssets({
        items: stationaryAssetsResponse.data,
        count: stationaryAssetsResponse.data?.length,
      });
      setGateways({
        items: gatewaysResponse.data,
        count: gatewaysResponse.data?.length,
      });
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError({ message: `has been deleted.` });
      } else {
        console.error('Error fetching place data', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filtering data logic
  const onFilterChange = async (payload, queryState) => {
    setIsLoading(true);
    const formattedTabKey = tabKey.toLowerCase().replace(/\s+/g, '-');

    try {
      if (tabKey === TAB_KEYS_MAP.ASSETS) {
        const { data } = await Api.post(`/v2/${formattedTabKey}/filter/`, payload);
        setAssets(data);
      } else if (tabKey === TAB_KEYS_MAP.STATIONARY_ASSETS) {
        const { data } = await Api.post(`/v2/places/${place.id}/${formattedTabKey}`, payload);
        setStationaryAssets({ items: data, count: data?.length });
      } else if (tabKey === TAB_KEYS_MAP.GATEWAYS) {
        const gatewayPayload = payload ?? {
          sorts: queryState?.setSort?.() ?? [{ field: 'name', ascending: true }],
        };
        const { data } = await Api.post(
          `/v2/places/${place.id}/${formattedTabKey}/filter`,
          gatewayPayload,
        );
        setGateways({ items: data, count: data?.length });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // #region FAVORITE & DELETE ACTIONS
  const favoritePlace = async () => {
    await favoriteService.favoritePlace(placeId);
    checkIsFavorite();
  };

  const unfavoritePlace = async () => {
    await favoriteService.unfavoritePlace(placeId);
    checkIsFavorite();
  };

  const checkIsFavorite = async () => {
    const { data } = await favoriteService.getFavorites(app.id);
    const favorite = data.find((fav) => fav.itemType === 'Place' && fav.itemId === Number(placeId));
    setIsFavorite(!!favorite);
  };

  const deletePlace = async () => {
    const result = await MySwal.fire({
      title: `Are you sure?`,
      text: 'This place will be deleted?',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'No',
      confirmButtonText: 'Yes',
    });

    if (result.isConfirmed) {
      try {
        await Api.delete(`/places/${placeId}`);
        props.history.push('/places');
        toast.success('Place was successfully deleted!');
      } catch (error) {
        toast.error('Failed to delete the Place.');
      }
    }
  };
  // #endregion

  // Consolidated map marker click handler
  const onMapItemClicked = useCallback(
    ({ node }, type) => {
      const mapLayer = mapLayers?.find((layer) => {
        const layerItem = layer.data[type];
        return (
          layerItem === node.data ||
          (layerItem?.id != null && layerItem.id === node.data?.id) ||
          (layerItem?.name != null && layerItem.name === node.data?.name)
        );
      });
      if (!mapLayer) return;

      const item = mapLayer.data[type];
      const isAsset = type === 'asset';
      const hasName = type === 'asset' || type === 'gateway';
      const nameOrId = hasName ? item.name : `Id: ${item.id}`;
      const copyValue = hasName ? item.name : item.id;

      const actionButton = isAsset
        ? `<hr/>
           <div class="row">
             <div class="col text-center" style="text-align:center;">
               <span class="btn btn-primary btn-sm" type="button" onclick="window.markers.locations?.[${item.id}]()">Go to Asset</span>
             </div>
           </div>`
        : '';

      mapLayer
        .bindPopup(
          `<br/>
          <div class='d-flex flex-row align-items-center'>
            <strong class='col-10'>${nameOrId}</strong> 
            <a class='btn-light d-flex justify-content-left align-items-center' href='#' onclick="CBCopy('${copyValue}')"> 
              <i class="col-1 far fa-lg fa-copy" ></i> 
            </a>
          </div>
          ${actionButton}`,
          { autoPan: true },
        )
        .openPopup();
    },
    [mapLayers],
  );

  // #region RENDER ELEMENTS
  const renderAssetsGrid = () => (
    <ServerSideGrid
      id="assetGrid"
      initQueryParams={new PagedQueryParams()}
      assetFilters={assetFilters}
      isLoading={isLoading}
      columnDefs={assetColumnDefs}
      handleFetchData={(payload) => {
        defaultAssetFilter.forEach((item) => payload.filters.push(item));
        onFilterChange(payload);
      }}
      rowData={assets || []}
      ignoreFetch={false}
      disabledFilters={['all', 'map', 'place', 'device id', 'battery value']}
      filterControl={true}
      searchControl={true}
      viewingControl={true}
      pagerControl={true}
      exportControl={false}
      gridProps={{
        frameworkComponents: {
          OnlineStatusCellRenderer,
          LinkCellRenderer,
          BeaconReadingsCellRenderer,
        },
        onCellClicked: (e) => onMapItemClicked(e, 'asset'),
        getRowNodeId: (data) => data.id,
      }}
    />
  );

  const renderStationaryAssetsGrid = () => (
    <ServerSideGrid
      id="stationaryAssetsGrid"
      initQueryParams={new PagedQueryParams()}
      assetFilters={assetFilters}
      isLoading={isLoading}
      columnDefs={stationaryAssetsColumnDefs}
      handleFetchData={(payload = { filters: [] }) => {
        payload.filters = payload.filters || [];

        defaultAssetFilter.forEach((item) => {
          const alreadyExists = payload.filters.some(
            (f) => f.field === item.field && f.type === item.type && f.filter === item.filter,
          );
          if (!alreadyExists) payload.filters.push(item);
        });

        const sortField = payload?.sorts?.[0]?.field;
        const isAbilitySort = !isNaN(Number(sortField));
        const currentSearch = payload?.search ?? '';
        const searchChanged = currentSearch !== previousStationarySearchRef.current;

        previousStationarySearchRef.current = currentSearch;

        if (isAbilitySort && !searchChanged) return;

        if (payload?.sorts?.length) {
          payload.sorts = payload.sorts.map((sort) => ({
            ...sort,
            field: sort.field === 'name' ? 'assetName' : sort.field,
          }));
        }

        onFilterChange(payload);
      }}
      rowData={stationaryAssets || []}
      ignoreFetch={false}
      disabledFilters={[
        'all',
        'map',
        'place',
        'active',
        'single asset',
        'keyword',
        'identifier',
        'device id',
        'battery value',
      ]}
      filterControl={true}
      searchControl={true}
      viewingControl={true}
      pagerControl={true}
      exportControl={false}
      gridProps={{
        onCellClicked: (e) => onMapItemClicked(e, 'beacon'),
        getRowNodeId: (data) => data.id,
      }}
    />
  );

  const renderGatewaysGrid = () => (
    <ServerSideGrid
      id="GatewaysGrid"
      initQueryParams={new PagedQueryParams()}
      isLoading={isLoading}
      columnDefs={gatewaysColumnDefs}
      rowData={gateways}
      handleFetchData={(payload, isCSV, queryState) => onFilterChange(payload, queryState)}
      ignoreFetch={false}
      filterControl={false}
      searchControl={false}
      viewingControl={false}
      pagerControl={false}
      exportControl={false}
      gridProps={{
        onCellClicked: (e) => onMapItemClicked(e, 'gateway'),
        getRowNodeId: (data) => data.id,
      }}
    />
  );

  const renderGrid = () => {
    switch (tabKey) {
      case TAB_KEYS_MAP.ASSETS:
        return renderAssetsGrid();
      case TAB_KEYS_MAP.STATIONARY_ASSETS:
        return renderStationaryAssetsGrid();
      case TAB_KEYS_MAP.GATEWAYS:
        return renderGatewaysGrid();
      default:
        return null;
    }
  };

  const renderMap = () => {
    switch (tabKey) {
      case TAB_KEYS_MAP.ASSETS:
        return (
          <LocationMap
            id="assetsMap"
            key="assetsMap"
            map={map}
            locations={assetLocations}
            height="100%"
            onSetLocationLayers={setMapLayers}
          />
        );
      case TAB_KEYS_MAP.STATIONARY_ASSETS:
        return (
          <div className="h-100 place-detail-stationary-asset-map-wrap">
            <Tabs activeKey={abilityTabKey} onSelect={setAbilityTabKey}>
              {ABILITIES.filter((ability) =>
                ['Temperature', 'Humidity', 'Location', 'Gateway Health Check'].includes(ability),
              ).map((ability) => (
                <Tab
                  key={ability}
                  eventKey={ability}
                  title={
                    <Icon variant="dark" name={ability.toLowerCase()} title={ability} showPointer />
                  }
                />
              ))}
            </Tabs>
            <LocationMap
              id="stationaryAssetsMap"
              key="stationaryAssetsMap"
              map={map}
              sAsset={stationaryAssetsLocations}
              reading={abilityTabKey}
              height="85%"
              onSetLayers={setMapLayers}
            />
          </div>
        );
      case TAB_KEYS_MAP.GATEWAYS:
        return (
          <LocationMap
            id="gatewaysMap"
            key="gatewaysMap"
            map={map}
            gateways={gatewayLocations}
            height="100%"
            onSetLayers={setMapLayers}
          />
        );
      default:
        return null;
    }
  };
  // #endregion RENDER ELEMENTS

  if (error) {
    return (
      <div className="h-100 d-flex flex-column justify-content-around align-items-center">
        <Text as="h1" variant="danger" className="fs-2x">
          The Place <span className="place-name-primary">{placeName}</span> {error.message}
        </Text>
        <Button className="retry-btn" onClick={() => history.goBack()}>
          Return to Previous Page
        </Button>
      </div>
    );
  }

  if (place === null) {
    return <Loading />;
  }

  return (
    <Card className="h-100 places-detail-header">
      <Card.Header>
        <Card.Title>
          <Row>
            <Col className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-column">
                <Text className="fs-md mb-1">{place?.name}</Text>
                <Text className="fs-sm fw-light">Place ID: {placeId}</Text>
              </div>
              <div className="d-flex flex-row align-items-center justify-content-end">
                <Icon
                  name={isFavorite ? 'star-fill' : 'star-empty'}
                  size="lg"
                  variant="light"
                  className="font-weight-bold place-detail-favorite-icon"
                  showPointer={true}
                  disabled={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    isFavorite ? unfavoritePlace() : favoritePlace();
                  }}
                />
                {!isReadOnly() && (
                  <DetailOptionsOverlay onClickDelete={deletePlace} showEditBtn={false} />
                )}
              </div>
            </Col>
          </Row>
        </Card.Title>
        <Row>
          <Col>
            <Tabs activeKey={tabKey} onSelect={setTabKey}>
              {TAB_KEYS.map((key) => (
                <Tab key={key} eventKey={key} title={key} />
              ))}
            </Tabs>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="h-100">
        <Row className="h-100">
          <Col className="place-grid">{renderGrid()}</Col>
          <Col className="place-map">{renderMap()}</Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

export default PlaceDetailScreen;
